import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { baseStyleSheet } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { CustomDateRange } from '../../../CustomDateRange';
import { Dropdown } from '../../../Dropdown';
import { DisclosureIcon } from '../../../svgs/icons/DisclosureIcon';
import { styleSheet as dropdownStyles } from '../../DropdownComponent/styles';
import { DonationDateRangeOption } from '../models';
import { IDonationDayOption, getDonationDayOptions } from '../utils';
import { styleSheet } from './styles';

export const DonationDateDropdown = ({
	label,
	minimumDate,
	setMinimumDate,
	maximumDate,
	setMaximumDate,
	showFirstDonationCheckbox = false,
	isFirstDonation = false,
	setIsFirstDonation,
	anchorClassName,
}: {
	label: string;
	minimumDate: string;
	setMinimumDate: (d: string) => void;
	maximumDate: string;
	setMaximumDate: (d: string) => void;
	showFirstDonationCheckbox?: boolean;
	isFirstDonation?: boolean;
	setIsFirstDonation?: (v: boolean) => void;
	anchorClassName?: string;
}) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [selectedDayOption, setSelectedDayOption] = React.useState<IDonationDayOption | null>(null);
	const donationDayOptions = React.useMemo(() => getDonationDayOptions(), []);

	const getDropdownText = () => {
		if (!minimumDate || !selectedDayOption) {
			return label;
		}
		if (isFirstDonation) {
			return `First Donation/${selectedDayOption.label}`;
		}
		return `${label}/${selectedDayOption.label}`;
	};

	const selectDateRangeOption = (newDayOption: IDonationDayOption, ev: React.MouseEvent<HTMLButtonElement>) => {
		if (newDayOption.value === DonationDateRangeOption.Custom) {
			setSelectedDayOption(newDayOption);
			ev.stopPropagation();
			ev.preventDefault();
		} else {
			setMinimumDate(newDayOption.value);
			setMaximumDate(newDayOption.maxValue);
			setSelectedDayOption(newDayOption);
			setIsOpen(false);
		}
	};

	React.useEffect(() => {
		if (maximumDate) {
			// Check if it matches a preset option with maxValue (ThisYear/LastYear)
			const presetMatch = donationDayOptions.find(
				option => option.maxValue && option.value === minimumDate && option.maxValue === maximumDate
			);
			if (presetMatch) {
				setSelectedDayOption(presetMatch);
				return;
			}
			const customDayOption = donationDayOptions.find(option => option.value === DonationDateRangeOption.Custom);
			setSelectedDayOption(customDayOption);
			return;
		}
		const match = donationDayOptions.find((option: IDonationDayOption) => {
			return option.value === minimumDate;
		});
		setSelectedDayOption(match);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [minimumDate, maximumDate]);

	const clearDateSearch = () => {
		setSelectedDayOption(null);
		setMinimumDate('');
		setMaximumDate('');
		if (setIsFirstDonation) {
			setIsFirstDonation(false);
		}
		setIsOpen(false);
	};

	const isCustomSelected = selectedDayOption?.value === DonationDateRangeOption.Custom;

	return (
		<Dropdown
			anchor={
				<div className={css(dropdownStyles.dropdownAnchor)}>
					<span className={css(baseStyleSheet.truncateText)}>{getDropdownText()}</span>
					<DisclosureIcon className={css(dropdownStyles.dropdownIcon)} />
				</div>
			}
			anchorClassName={anchorClassName}
			disabled={false}
			className={css(dropdownStyles.dropdownInline, isOpen ? dropdownStyles.dropdownInlineOpen : null)}
			contentClassName={css(dropdownStyles.dropdownMenu)}
			contentPositionY='bottom'
			contentInnerClassName={css(styleSheet.dropdownContentInnerOverrides)}
			isOpen={isOpen}
			onOpenChanged={() => setIsOpen(!isOpen)}
			openOnClick
		>
			<div className={css(dropdownStyles.dropdown)}>
				{showFirstDonationCheckbox && (
					<div className={css(styleSheet.firstDonationCheckbox)}>
						<Checkbox
							checked={isFirstDonation}
							id={`first-donation-checkbox-${label.replace(/\s/g, '-')}`}
							onChange={() => setIsFirstDonation?.(!isFirstDonation)}
						>
							<span>Show first donations only</span>
						</Checkbox>
					</div>
				)}

				{showFirstDonationCheckbox && <div className={css(dropdownStyles.dropdownSeparator)} />}

				<div>
					{isCustomSelected ? (
						<CustomDateRange
							endDateRequired
							resetDates={() => {
								setMinimumDate('');
								setMaximumDate('');
							}}
							from={minimumDate ? moment(minimumDate).toDate() : null}
							to={maximumDate ? moment(maximumDate).toDate() : null}
							onChange={(from, to) => {
								if (!from || !to) {
									return;
								}
								setMinimumDate(moment(from).format('YYYY-MM-DD'));
								setMaximumDate(moment(to).format('YYYY-MM-DD'));
							}}
							disableScrollTo
						/>
					) : null}

					{!isCustomSelected &&
						donationDayOptions.map(dayOption => {
							return (
								<button
									key={`donation-date-range-option-${label}-${dayOption.key}`}
									onClick={(e: React.MouseEvent<HTMLButtonElement>) => selectDateRangeOption(dayOption, e)}
									className={css(
										dropdownStyles.option,
										selectedDayOption?.key === dayOption.key ? dropdownStyles.selected : null
									)}
								>
									{dayOption.label}
								</button>
							);
						})}
				</div>

				<div className={css(dropdownStyles.dropdownSeparator)} />

				{minimumDate || isCustomSelected ? (
					<button className={css(baseStyleSheet.ctaButton)} onClick={clearDateSearch}>
						Reset
					</button>
				) : null}
			</div>
		</Dropdown>
	);
};
